/**
 * Webhook Form Utilities
 * Shared utilities for forms that submit to Flow Webhooks
 *
 * Usage:
 *   const utils = new WebhookFormUtils(config);
 *   const response = await utils.submitToWebhook(payload);
 *
 *   const uiUtils = new FormUIUtils(submitButton, successElement, errorElement);
 *   uiUtils.setButtonLoading(true);
 *   uiUtils.showMessage('success', 'Form submitted successfully!');
 */

/**
 * WebhookFormUtils - Handles webhook submission via Cloudflare Worker proxy
 */
class WebhookFormUtils {
  /**
   * @param {Object} config - Webhook configuration
   * @param {string} config.proxyUrl - The Cloudflare Worker proxy base URL (from metafield)
   * @param {string} config.formSlug - The form slug (e.g., 'quote-request', 'return-request')
   */
  constructor(config = {}) {
    this.proxyUrl = config.proxyUrl || '';
    this.formSlug = config.formSlug || '';
  }

  /**
   * Parse configuration from window object
   * @param {string} configKey - Key name in window object (e.g., 'QUOTE_FORM_CONFIG')
   * @returns {WebhookFormUtils} New instance with parsed config
   */
  static parseConfig(configKey) {
    const config = window[configKey] || {};
    return new WebhookFormUtils({
      proxyUrl: config.proxyUrl || '',
      formSlug: config.formSlug || ''
    });
  }

  /**
   * Validate proxy configuration
   * @returns {boolean} True if configuration is valid
   * @throws {Error} If configuration is invalid
   */
  validateConfig() {
    if (!this.proxyUrl) {
      throw new Error('Form proxy URL not configured. Please contact support.');
    }

    if (!this.formSlug) {
      throw new Error('Form type not configured. Please contact support.');
    }

    try {
      new URL(this.proxyUrl);
      return true;
    } catch (_e) {
      throw new Error('Invalid proxy URL format. Please contact support.');
    }
  }

  /**
   * Build request configuration for Cloudflare Worker proxy
   * @param {Object} payload - The payload to send
   * @returns {Object} Request configuration object with url, headers, and body
   */
  buildRequestConfig(payload) {
    const headers = {
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    };

    // Build proxy URL with form slug
    const proxyUrl = `${this.proxyUrl}/${this.formSlug}`;

    // Extract form data from payload.payload or payload
    const formData = payload.payload || payload;

    return {
      url: proxyUrl,
      config: {
        method: 'POST',
        headers: headers,
        body: JSON.stringify(formData)
      }
    };
  }

  /**
   * Submit payload to Cloudflare Worker proxy
   * @param {Object} payload - The data to submit
   * @returns {Promise<Response>} Fetch response object
   * @throws {Error} If submission fails
   */
  async submitToWebhook(payload) {
    // Validate configuration before submission
    this.validateConfig();

    // Build request configuration
    const { url, config } = this.buildRequestConfig(payload);

    try {
      const response = await fetch(url, config);
      return response;
    } catch (error) {
      console.error('Fetch error:', error);
      throw new Error('Unable to connect to form submission service. Please try again.');
    }
  }
}

// Register WebhookFormUtils on the window ASAP — right after the class
// closes, before the larger FormUIUtils class below — so that even if the
// rest of this file fails to evaluate (network truncation, CSP, extension
// blocking, etc.), dependent scripts can still find the class.
// Fixes SHOPIFY-1SQ: ReferenceError: WebhookFormUtils is not defined fired
// 16 times over 3 months when this file didn't reach the bottom export.
if (typeof window !== 'undefined') {
  window.WebhookFormUtils = WebhookFormUtils;
}

/**
 * FormUIUtils - Handles common form UI operations
 */
class FormUIUtils {
  /**
   * @param {HTMLElement} submitButton - The form submit button
   * @param {HTMLElement} successElement - Success message element
   * @param {HTMLElement} errorElement - Error message element
   * @param {HTMLElement} errorTextElement - Error text content element (optional)
   */
  constructor(submitButton, successElement, errorElement, errorTextElement = null) {
    this.submitButton = submitButton;
    this.successElement = successElement;
    this.errorElement = errorElement;
    this.errorTextElement = errorTextElement || errorElement;
  }

  /**
   * Set button loading state
   * @param {boolean} isLoading - True to show loading, false to hide
   */
  setButtonLoading(isLoading) {
    if (!this.submitButton) return;

    const btnText = this.submitButton.querySelector('.btn__text');
    const btnSpinner = this.submitButton.querySelector('.btn__spinner');

    if (isLoading) {
      this.submitButton.disabled = true;
      if (btnText) btnText.style.display = 'none';
      if (btnSpinner) btnSpinner.style.display = 'inline-block';
    } else {
      this.submitButton.disabled = false;
      if (btnText) btnText.style.display = '';
      if (btnSpinner) btnSpinner.style.display = 'none';
    }
  }

  /**
   * Show a message to the user
   * @param {string} type - Message type: 'success', 'error', or 'info'
   * @param {string} message - Message text to display
   * @param {Object} options - Additional options
   * @param {number} options.autoCloseDelay - Auto-close modal after N milliseconds (0 = no auto-close)
   * @param {boolean} options.scrollIntoView - Scroll message into view (default: true)
   */
  showMessage(type, message, options = {}) {
    const {
      autoCloseDelay = 0,
      scrollIntoView = true
    } = options;

    // Hide all messages first
    this.hideMessage();

    let element;
    switch (type) {
      case 'success':
        element = this.successElement;
        if (this.errorTextElement && this.errorTextElement !== this.errorElement) {
          // Clear error text if separate element
          this.errorTextElement.textContent = '';
        }
        break;
      case 'error':
        element = this.errorElement;
        if (this.errorTextElement) {
          this.errorTextElement.textContent = message;
        }
        break;
      case 'info':
        // Info messages use the error element styled differently
        element = this.errorElement;
        if (this.errorTextElement) {
          this.errorTextElement.textContent = message;
        }
        break;
      default:
        console.warn(`Unknown message type: ${type}`);
        return;
    }

    if (!element) return;

    // Show the message
    element.style.display = 'block';
    element.focus();

    // Scroll into view
    if (scrollIntoView) {
      element.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }

    // Auto-close modal if requested
    if (autoCloseDelay > 0 && type === 'success') {
      setTimeout(() => {
        this.closeModal();
      }, autoCloseDelay);
    }
  }

  /**
   * Hide all messages
   */
  hideMessage() {
    if (this.successElement) {
      this.successElement.style.display = 'none';
    }
    if (this.errorElement) {
      this.errorElement.style.display = 'none';
    }
  }

  /**
   * Close the parent modal/drawer
   * Finds the parent basic-modal custom element and triggers close
   */
  closeModal() {
    const form = this.submitButton?.closest('form');
    if (!form) return;

    const modal = form.closest('basic-modal');
    if (modal) {
      const closeButton = modal.querySelector('.drawer__close-btn');
      if (closeButton) {
        closeButton.click();
      }
    }
  }

  /**
   * Validate required fields in a form
   * Uses HTML5 validation API with custom enhancements
   * @param {HTMLFormElement} form - The form to validate
   * @param {boolean} visibleOnly - Only validate visible required fields (default: true)
   * @returns {boolean} True if form is valid
   */
  validateForm(form, visibleOnly = true) {
    if (!form) return false;

    // Clear previous errors
    this.clearAllFieldErrors(form);

    // Get all form inputs (not just required)
    const inputs = form.querySelectorAll('input, select, textarea');
    let firstInvalidField = null;
    let isValid = true;

    inputs.forEach(field => {
      // Skip honeypot fields
      if (field.hasAttribute('data-honeypot')) return;

      // Skip hidden fields if visibleOnly
      if (visibleOnly && field.type === 'hidden') return;

      // Check if field is visually hidden
      if (visibleOnly) {
        const style = window.getComputedStyle(field);
        if (style.display === 'none' || style.visibility === 'hidden') {
          return;
        }
        // Also check parent visibility
        const parent = field.closest('.form-field');
        if (parent) {
          const parentStyle = window.getComputedStyle(parent);
          if (parentStyle.display === 'none' || parentStyle.visibility === 'hidden') {
            return;
          }
        }
      }

      // Run HTML5 validation
      const fieldValid = this.validateField(field);
      if (!fieldValid) {
        isValid = false;
        if (!firstInvalidField) {
          firstInvalidField = field;
        }
      }
    });

    // Focus first invalid field and show browser validation UI
    if (!isValid && firstInvalidField) {
      firstInvalidField.focus();
      form.reportValidity();
    }

    return isValid;
  }

  /**
   * Validate a single form field
   * @param {HTMLElement} field - The field to validate
   * @returns {boolean} True if field is valid
   */
  validateField(field) {
    // Clear any previous custom validity before checking
    field.setCustomValidity('');

    // Use HTML5 checkValidity first
    if (!field.checkValidity()) {
      this.showFieldError(field.id, field.validationMessage);
      return false;
    }

    // Additional custom validations
    const value = field.value.trim();

    // Email validation (enhanced)
    if (field.type === 'email' && value) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(value)) {
        this.showFieldError(field.id, 'Please enter a valid email address');
        field.setCustomValidity('Please enter a valid email address');
        return false;
      }
    }

    // Phone validation (basic - allows common formats)
    if (field.type === 'tel' && value) {
      // Remove common formatting characters for validation
      const digitsOnly = value.replace(/[\s()+-]/g, '').replace(/\./g, '');
      if (digitsOnly.length > 0 && (digitsOnly.length < 7 || !/^\d+$/.test(digitsOnly))) {
        this.showFieldError(field.id, 'Please enter a valid phone number');
        field.setCustomValidity('Please enter a valid phone number');
        return false;
      }
    }

    // Clear any custom validity
    field.setCustomValidity('');
    this.clearFieldError(field.id);
    return true;
  }

  /**
   * Setup real-time validation on field blur
   * @param {HTMLFormElement} form - The form to setup validation for
   */
  setupRealtimeValidation(form) {
    if (!form) return;

    const inputs = form.querySelectorAll('input, select, textarea');

    inputs.forEach(field => {
      // Skip honeypot
      if (field.hasAttribute('data-honeypot')) return;

      // Validate on blur
      field.addEventListener('blur', () => {
        if (field.value.trim() || field.hasAttribute('required')) {
          this.validateField(field);
        }
      });

      // Clear error on input
      field.addEventListener('input', () => {
        if (field.hasAttribute('aria-invalid')) {
          this.validateField(field);
        }
      });
    });
  }

  /**
   * Show inline error message for a specific field
   * @param {string} fieldId - The ID of the field element
   * @param {string} message - Error message to display
   */
  showFieldError(fieldId, message) {
    const field = document.getElementById(fieldId);
    if (!field) return;

    // Create or update error message element
    let errorEl = document.getElementById(`${fieldId}-error`);
    if (!errorEl) {
      errorEl = document.createElement('div');
      errorEl.id = `${fieldId}-error`;
      errorEl.className = 'field-error';
      errorEl.setAttribute('role', 'alert');

      // Insert error element after the field or its parent wrapper
      const fieldWrapper = field.closest('.form-field') || field.parentNode;
      fieldWrapper.appendChild(errorEl);

      // Link field to error with aria-describedby
      field.setAttribute('aria-describedby', errorEl.id);
      field.setAttribute('aria-invalid', 'true');
    }

    errorEl.textContent = message;
  }

  /**
   * Clear inline error message for a specific field
   * @param {string} fieldId - The ID of the field element
   */
  clearFieldError(fieldId) {
    const field = document.getElementById(fieldId);
    const errorEl = document.getElementById(`${fieldId}-error`);

    if (errorEl) {
      errorEl.remove();
    }

    if (field) {
      field.removeAttribute('aria-describedby');
      field.removeAttribute('aria-invalid');
    }
  }

  /**
   * Clear all field errors in a form
   * @param {HTMLFormElement} form - The form to clear errors from
   */
  clearAllFieldErrors(form) {
    if (!form) return;

    const errorElements = form.querySelectorAll('.field-error');
    errorElements.forEach(errorEl => errorEl.remove());

    const invalidFields = form.querySelectorAll('[aria-invalid="true"]');
    invalidFields.forEach(field => {
      field.removeAttribute('aria-describedby');
      field.removeAttribute('aria-invalid');
    });
  }

  /**
   * Sanitize text to prevent malicious content in emails
   * @param {string} text - Text to sanitize
   * @returns {string} Sanitized text
   */
  sanitizeForEmail(text) {
    if (!text || typeof text !== 'string') return '';

    // Remove any HTML/script tags
    let sanitized = text.replace(/<[^>]*>/g, '');

    // Remove potentially dangerous characters and patterns
    sanitized = sanitized.replace(/[<>]/g, '');  // Remove angle brackets
    sanitized = sanitized.replace(/javascript:/gi, '');  // Remove javascript: protocol
    sanitized = sanitized.replace(/on\w+\s*=/gi, '');  // Remove event handlers
    sanitized = sanitized.replace(/data:/gi, '');  // Remove data: protocol
    sanitized = sanitized.replace(/vbscript:/gi, '');  // Remove vbscript: protocol

    // Limit length to prevent abuse
    const maxLength = 500;
    if (sanitized.length > maxLength) {
      sanitized = sanitized.substring(0, maxLength) + '...';
    }

    return sanitized.trim();
  }

  /**
   * Show email fallback message with mailto link
   * @param {Object} formData - Form data to include in email
   * @param {string} emailAddress - Fallback email address (default: smile@lamps.com)
   * @param {string} subject - Email subject line
   */
  showEmailFallback(formData, emailAddress = 'smile@lamps.com', subject = 'Form Submission') {
    // Add [FORM FALLBACK] prefix to subject to indicate this came from the fallback system
    const fallbackSubject = `[FORM FALLBACK] ${subject}`;

    // Build email body from form data with security sanitization
    let emailBody = '⚠️ AUTOMATED FORM FALLBACK SUBMISSION\n';
    emailBody += 'This submission was sent via email because the automated form system was unavailable.\n';
    emailBody += 'Timestamp: ' + new Date().toISOString() + '\n\n';
    emailBody += '--- FORM DATA ---\n\n';

    // Sanitize and format form data
    for (const [key, value] of Object.entries(formData)) {
      if (value && value !== '') {
        // Format key as readable label
        const label = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());

        // Sanitize the value to prevent malicious content
        const sanitizedValue = this.sanitizeForEmail(value);

        emailBody += `${label}: ${sanitizedValue}\n`;
      }
    }

    emailBody += '\n--- END FORM DATA ---\n';
    emailBody += '\nPlease process this request manually and follow up with the customer.';

    // Create mailto link with sanitized content
    const mailtoLink = `mailto:${emailAddress}?subject=${encodeURIComponent(fallbackSubject)}&body=${encodeURIComponent(emailBody)}`;

    // Escape email address for display (prevent XSS)
    const escapeHtml = (text) => {
      const div = document.createElement('div');
      div.textContent = text;
      return div.innerHTML;
    };
    const safeEmailAddress = escapeHtml(emailAddress);

    // Show message with email fallback
    const message = `
      <div class="form-fallback-message">
        <p><strong>We're currently experiencing technical difficulties with our form submission system.</strong></p>
        <p>We're working on a fix. In the meantime, you can:</p>
        <div style="margin: 20px 0;">
          <a href="${mailtoLink}" class="btn btn--secondary" style="display: block; width: 100%; max-width: 400px; margin: 0 auto; text-align: center; padding: 12px 20px; box-sizing: border-box; word-wrap: break-word;">
            Email Your Request To ${safeEmailAddress}
          </a>
        </div>
        <p style="text-align: center; margin-top: 15px;">Try submitting the form again later</p>
        <p><small>We apologize for the inconvenience and will resolve this as quickly as possible.</small></p>
      </div>
    `;

    // Display the fallback message
    if (this.errorTextElement) {
      this.errorTextElement.innerHTML = message;
    }

    if (this.errorElement) {
      this.errorElement.style.display = 'block';
      this.errorElement.classList.add('form-fallback-active');
      this.errorElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }

}

// Register FormUIUtils on the window. WebhookFormUtils is registered
// earlier (right after its class declaration) as a defense against
// partial-evaluation failures — see comment up there.
if (typeof window !== 'undefined') {
  window.FormUIUtils = FormUIUtils;
}
